/**
 * Liquidity Report Preview - Liquidez e Fluxo de Caixa
 * 5 Slides Premium Board-Ready para apresentação CFO → Board
 * 
 * Slide 1: Capa (apresentação + KPIs)
 * Slide 2: Fluxo de Caixa Operacional (waterfall EBITDA → FCO)
 * Slide 3: Posição de Liquidez (evolução caixa + rácios)
 * Slide 4: Dívida e Investimento (estrutura dívida + CAPEX)
 * Slide 5: Resumo Executivo (conclusões + prioridades)
 * 
 * Canvas: 1600 x 900 px
 */

import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { getLiquidityReportData } from '../../data/reportDataSource';

// ============ DESIGN TOKENS ============
const COLORS = {
  pageBg: '#0A192F',
  contentBg: '#F7F6F2',
  gold: '#C8A13A',
  textPrimary: '#0A192F',
  textSecondary: '#465467',
  textWhite: '#FFFFFF',
  positive: '#16A34A',
  negative: '#C41E6D',
};

const FONTS = {
  serif: '"Merriweather", Georgia, "Times New Roman", serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// Get centralized data
const centralizedData = getLiquidityReportData('Q1_2026');

// ============ SLIDE 1: CAPA ============
const CoverSlide = ({ data, language = 'pt' }) => {
  // Use centralized data with fallback
  const d = data || centralizedData || {
    fco: 1.6,
    fcoVsOrc: 0,
    fcoVs1T25: -80,
    liquidezCorrente: 2.1,
    liquidezVsOrc: 0.0,
    caixaDisponivel: 45.8,
    caixaVsOrc: 12,
    divLiqEbitda: 0.8,
    divLiqVsOrc: -0.2,
  };

  const kpis = [
    { label: language === 'pt' ? 'FLUXO DE CAIXA OPERACIONAL' : 'OPERATING CASH FLOW', value: `USD ${(d.fco || 0).toFixed(1)}M`, vsOrc: d.fcoVsOrc, vs1T25: d.fcoVs1T25 },
    { label: language === 'pt' ? 'LIQUIDEZ CORRENTE' : 'CURRENT RATIO', value: `${(d.liquidezCorrente || 0).toFixed(1)}x`, vsOrc: d.liquidezVsOrc, suffix: 'x' },
    { label: language === 'pt' ? 'CAIXA DISPONÍVEL' : 'AVAILABLE CASH', value: `USD ${(d.caixaDisponivel || 0).toFixed(1)}M`, vsOrc: d.caixaVsOrc },
    { label: language === 'pt' ? 'DÍVIDA LÍQ. / EBITDA' : 'NET DEBT / EBITDA', value: `${(d.divLiqEbitda || 0).toFixed(1)}x`, vsOrc: d.divLiqVsOrc, suffix: 'x', inverted: true },
  ];

  return (
    <div style={{ width: 1600, height: 900, backgroundColor: COLORS.pageBg, position: 'relative', fontFamily: FONTS.sans, overflow: 'hidden', display: 'flex' }}>
      {/* LEFT SIDE */}
      <div style={{ width: '45%', height: '100%', padding: '60px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 600, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: 16 }}>
          {language === 'pt' ? 'Relatório Executivo' : 'Executive Report'}
        </span>
        <h1 style={{ fontFamily: FONTS.serif, fontSize: 42, lineHeight: '52px', fontWeight: 700, color: COLORS.textWhite, margin: 0, marginBottom: 24 }}>
          {language === 'pt' ? 'Liquidez e Fluxo de Caixa' : 'Liquidity & Cash Flow'}
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 16, color: 'rgba(255,255,255,0.7)', margin: 0, marginBottom: 8 }}>{language === 'pt' ? 'Empresa ABC' : 'Company ABC'}</p>
        <p style={{ fontFamily: FONTS.sans, fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: 0, marginBottom: 32 }}>
          {language === 'pt' ? 'Relatório executivo de posição de liquidez e fluxos de caixa' : 'Executive report on liquidity position and cash flows'}
        </p>
        <div style={{ backgroundColor: 'rgba(200, 161, 58, 0.15)', borderLeft: `4px solid ${COLORS.gold}`, padding: '12px 20px', borderRadius: '0 8px 8px 0', marginBottom: 40 }}>
          <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600, color: COLORS.gold }}>
            {language === 'pt' ? 'Período: ' : 'Period: '}<span style={{ color: COLORS.textWhite }}>1.º trimestre de 2026</span>
          </span>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 32 }}>
          <p style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 12 }}>
            {language === 'pt' ? 'TESE EXECUTIVA' : 'EXECUTIVE THESIS'}
          </p>
          <p style={{ fontFamily: FONTS.serif, fontSize: 18, fontStyle: 'italic', color: COLORS.textWhite, lineHeight: 1.6, margin: 0 }}>
            {language === 'pt' 
              ? '"A conversão de EBITDA em caixa mantém-se robusta, com folga de liquidez que preserva capacidade de investimento e conforto do balanço."'
              : '"EBITDA to cash conversion remains robust, with liquidity headroom preserving investment capacity and balance sheet comfort."'}
          </p>
        </div>
      </div>

      {/* RIGHT SIDE - KPIs */}
      <div style={{ width: '55%', height: '100%', backgroundColor: COLORS.contentBg, padding: '60px 50px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 700, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '2px', margin: 0, marginBottom: 8 }}>
            {language === 'pt' ? 'INDICADORES DE LIQUIDEZ' : 'LIQUIDITY INDICATORS'}
          </h2>
          <p style={{ fontFamily: FONTS.sans, fontSize: 13, color: COLORS.textSecondary, margin: 0 }}>1.º Trimestre 2026</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, flex: 1 }}>
          {kpis.map((kpi, i) => (
            <div key={i} style={{ backgroundColor: '#FFFFFF', borderRadius: 12, padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 600, color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0, marginBottom: 12 }}>{kpi.label}</p>
              <p style={{ fontFamily: FONTS.serif, fontSize: 32, fontWeight: 700, color: COLORS.textPrimary, margin: 0, marginBottom: 16 }}>{kpi.value}</p>
              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary }}>vs Orç.</span>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, color: (kpi.inverted ? kpi.vsOrc < 0 : kpi.vsOrc > 0) ? COLORS.positive : COLORS.negative, marginLeft: 6 }}>
                    {kpi.vsOrc > 0 ? '+' : ''}{typeof kpi.vsOrc === 'number' ? kpi.vsOrc.toFixed(0) : kpi.vsOrc}{kpi.suffix || '%'}
                  </span>
                </div>
                {kpi.vs1T25 !== undefined && (
                  <div>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary }}>vs 1T25</span>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, color: kpi.vs1T25 > 0 ? COLORS.positive : COLORS.negative, marginLeft: 6 }}>
                      {kpi.vs1T25 > 0 ? '+' : ''}{typeof kpi.vs1T25 === 'number' ? kpi.vs1T25.toFixed(0) : kpi.vs1T25}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div style={{ backgroundColor: COLORS.pageBg, borderRadius: 12, padding: '20px 24px', marginTop: 24 }}>
          <p style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 700, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '1px', margin: 0, marginBottom: 8 }}>
            {language === 'pt' ? 'SÍNTESE' : 'SUMMARY'}
          </p>
          <p style={{ fontFamily: FONTS.sans, fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, margin: 0 }}>
            {language === 'pt' 
              ? 'Posição de liquidez confortável com FCO acima do orçamento, permitindo redução de dívida e financiamento do CAPEX.'
              : 'Comfortable liquidity position with FCO above budget, enabling debt reduction and CAPEX financing.'}
          </p>
        </div>
      </div>
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{language === 'pt' ? 'ABC | Liquidez e Fluxo de Caixa' : 'ABC | Liquidity & Cash Flow'}</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.gold, fontWeight: 600 }}>Board-ready</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>1</span>
      </div>
    </div>
  );
};

// ============ SLIDE 2: FLUXO DE CAIXA OPERACIONAL ============
const CashFlowSlide = ({ data, language = 'pt' }) => {
  // Use centralized data with fallback
  const cashFlowData = centralizedData?.cashFlowWaterfall || {};
  const d = data || {
    ebitda: cashFlowData.ebitda || 4.8,
    varCapitalGiro: cashFlowData.varCapitalGiro || -1.2,
    impostosPagos: cashFlowData.impostosPagos || -0.8,
    juros: cashFlowData.juros || -1.4,
    outrosOp: cashFlowData.outrosOp || 0,
    fco: cashFlowData.fco || 1.6,
  };

  const waterfallData = [
    { label: 'EBITDA', value: d.ebitda, isEndpoint: true },
    { label: 'Var. Capital Giro', value: d.varCapitalGiro, isEndpoint: false },
    { label: 'Impostos Pagos', value: d.impostosPagos, isEndpoint: false },
    { label: 'Juros Pagos', value: d.juros, isEndpoint: false },
    { label: 'Outros Operacionais', value: d.outrosOp, isEndpoint: false },
    { label: 'FCO', value: d.fco, isEndpoint: true },
  ];

  const maxVal = Math.max(d.ebitda, d.fco) * 1.2;
  const barHeight = 42;
  const barGap = 12;

  return (
    <div style={{ width: 1600, height: 900, backgroundColor: COLORS.pageBg, position: 'relative', fontFamily: FONTS.sans, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ position: 'absolute', left: 60, top: 48, right: 60 }}>
        <h1 style={{ fontFamily: FONTS.serif, fontSize: 34, fontWeight: 700, color: COLORS.textWhite, margin: 0 }}>
          {language === 'pt' ? 'Fluxo de Caixa Operacional' : 'Operating Cash Flow'}
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 15, color: '#64748B', marginTop: 12 }}>
          {language === 'pt' ? 'Conversão do EBITDA em caixa operacional — 1.º Trimestre 2026' : 'EBITDA to operating cash conversion — Q1 2026'}
        </p>
      </div>

      {/* Main Panel */}
      <div style={{ position: 'absolute', left: 60, top: 140, right: 60, bottom: 160, backgroundColor: COLORS.contentBg, borderRadius: 16, padding: '40px 50px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 30 }}>
          <div style={{ width: 4, height: 24, backgroundColor: COLORS.gold, borderRadius: 2 }} />
          <h2 style={{ fontFamily: FONTS.serif, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>
            {language === 'pt' ? 'Decomposição do Fluxo de Caixa (USD M)' : 'Cash Flow Breakdown (USD M)'}
          </h2>
        </div>

        {/* Waterfall Chart */}
        <div style={{ position: 'relative', marginTop: 20 }}>
          {waterfallData.map((item, i) => {
            const barWidth = (Math.abs(item.value) / maxVal) * 55;
            const isPositive = item.value >= 0;
            const barColor = item.isEndpoint ? '#1E1B4B' : (isPositive ? COLORS.positive : COLORS.negative);
            
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: barGap, height: barHeight }}>
                <div style={{ width: 160, textAlign: 'right', paddingRight: 20 }}>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, color: COLORS.textSecondary }}>{item.label}</span>
                </div>
                <div style={{ flex: 1, position: 'relative', height: barHeight }}>
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: `${barWidth}%`,
                    height: barHeight,
                    backgroundColor: barColor,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingRight: 16,
                  }}>
                    <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, color: '#FFFFFF' }}>
                      {isPositive ? '' : ''}{item.value.toFixed(1)}M
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Conversion Rate */}
        <div style={{ marginTop: 40, padding: '20px 24px', backgroundColor: 'rgba(200, 161, 58, 0.1)', borderRadius: 12, borderLeft: `4px solid ${COLORS.gold}` }}>
          <p style={{ fontFamily: FONTS.serif, fontSize: 16, fontStyle: 'italic', color: COLORS.textPrimary, margin: 0 }}>
            {language === 'pt' 
              ? `"Taxa de conversão EBITDA → FCO de ${Math.round((d.fco / d.ebitda) * 100)}%, acima do target de 55%. O capital de giro absorveu ${Math.abs(d.varCapitalGiro).toFixed(1)}M no trimestre."`
              : `"EBITDA → FCO conversion rate of ${Math.round((d.fco / d.ebitda) * 100)}%, above 55% target. Working capital absorbed ${Math.abs(d.varCapitalGiro).toFixed(1)}M in the quarter."`}
          </p>
        </div>
      </div>

      {/* Message Cards */}
      <div style={{ position: 'absolute', left: 60, bottom: 56, right: 60, display: 'flex', gap: 20 }}>
        {[
          { num: '1', title: language === 'pt' ? 'Conversão robusta' : 'Robust conversion', text: language === 'pt' ? 'FCO/EBITDA acima do target histórico' : 'FCO/EBITDA above historical target' },
          { num: '2', title: language === 'pt' ? 'Capital de giro controlado' : 'Working capital controlled', text: language === 'pt' ? 'Variação sazonal dentro do esperado' : 'Seasonal variation within expectations' },
          { num: '3', title: language === 'pt' ? 'Impostos alinhados' : 'Taxes aligned', text: language === 'pt' ? 'Pagamentos conforme cronograma fiscal' : 'Payments per tax schedule' },
        ].map((msg, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 8, padding: '16px 20px', borderTop: `3px solid ${COLORS.gold}` }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: COLORS.pageBg, color: COLORS.textWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{msg.num}</div>
            <p style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, margin: 0, marginBottom: 4 }}>{msg.title}</p>
            <p style={{ fontFamily: FONTS.sans, fontSize: 12, color: COLORS.textSecondary, margin: 0 }}>{msg.text}</p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>{language === 'pt' ? 'ABC | Liquidez e Fluxo de Caixa' : 'ABC | Liquidity & Cash Flow'}</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.gold, fontWeight: 600 }}>Board-ready</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>2</span>
      </div>
    </div>
  );
};

// ============ SLIDE 3: POSIÇÃO DE LIQUIDEZ ============
const LiquidityPositionSlide = ({ data, language = 'pt' }) => {
  const d = data || {
    caixaInicio: 38.2,
    fco: 18.2,
    capex: -8.5,
    dividaAmort: -5.2,
    dividendos: -3.5,
    outros: 1.6,
    caixaFim: 40.8,
    linhaCredito: 25.0,
    liquidezTotal: 65.8,
  };

  const ratios = [
    { label: language === 'pt' ? 'Liquidez Corrente' : 'Current Ratio', value: '2.1x', target: '> 1.5x', status: 'ok' },
    { label: language === 'pt' ? 'Liquidez Imediata' : 'Quick Ratio', value: '1.4x', target: '> 1.0x', status: 'ok' },
    { label: language === 'pt' ? 'Cobertura Juros' : 'Interest Coverage', value: '8.2x', target: '> 4.0x', status: 'ok' },
    { label: language === 'pt' ? 'Dívida Líq./EBITDA' : 'Net Debt/EBITDA', value: '0.8x', target: '< 2.0x', status: 'ok' },
  ];

  return (
    <div style={{ width: 1600, height: 900, backgroundColor: COLORS.pageBg, position: 'relative', fontFamily: FONTS.sans, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ position: 'absolute', left: 60, top: 48, right: 60 }}>
        <h1 style={{ fontFamily: FONTS.serif, fontSize: 34, fontWeight: 700, color: COLORS.textWhite, margin: 0 }}>
          {language === 'pt' ? 'Posição de Liquidez' : 'Liquidity Position'}
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 15, color: '#64748B', marginTop: 12 }}>
          {language === 'pt' ? 'Evolução do caixa e rácios de cobertura — 1.º Trimestre 2026' : 'Cash evolution and coverage ratios — Q1 2026'}
        </p>
      </div>

      {/* Left Panel: Cash Bridge */}
      <div style={{ position: 'absolute', left: 60, top: 140, width: '55%', bottom: 100, backgroundColor: COLORS.contentBg, borderRadius: 16, padding: '30px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 4, height: 24, backgroundColor: COLORS.gold, borderRadius: 2 }} />
          <h2 style={{ fontFamily: FONTS.serif, fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>
            {language === 'pt' ? 'Evolução do Caixa (USD M)' : 'Cash Evolution (USD M)'}
          </h2>
        </div>

        {/* Cash Bridge Items */}
        {[
          { label: language === 'pt' ? 'Caixa Início' : 'Opening Cash', value: d.caixaInicio, isEndpoint: true },
          { label: language === 'pt' ? '+ FCO' : '+ FCO', value: d.fco, isEndpoint: false },
          { label: language === 'pt' ? '- CAPEX' : '- CAPEX', value: d.capex, isEndpoint: false },
          { label: language === 'pt' ? '- Amortização Dívida' : '- Debt Amortization', value: d.dividaAmort, isEndpoint: false },
          { label: language === 'pt' ? '- Dividendos' : '- Dividends', value: d.dividendos, isEndpoint: false },
          { label: language === 'pt' ? '± Outros' : '± Other', value: d.outros, isEndpoint: false },
          { label: language === 'pt' ? 'Caixa Fim' : 'Closing Cash', value: d.caixaFim, isEndpoint: true },
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, padding: '8px 0', borderBottom: i < 6 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
            <span style={{ flex: 1, fontFamily: FONTS.sans, fontSize: 12, fontWeight: item.isEndpoint ? 700 : 400, color: COLORS.textPrimary }}>{item.label}</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, color: item.isEndpoint ? COLORS.textPrimary : (item.value >= 0 ? COLORS.positive : COLORS.negative) }}>
              {item.value >= 0 && !item.isEndpoint ? '+' : ''}{item.value.toFixed(1)}M
            </span>
          </div>
        ))}

        {/* Available Liquidity Box */}
        <div style={{ marginTop: 20, padding: '16px 20px', backgroundColor: COLORS.pageBg, borderRadius: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{language === 'pt' ? 'Caixa Disponível' : 'Available Cash'}</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, color: COLORS.textWhite }}>{d.caixaFim.toFixed(1)}M</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{language === 'pt' ? '+ Linhas de Crédito' : '+ Credit Lines'}</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, color: COLORS.textWhite }}>{d.linhaCredito.toFixed(1)}M</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <span style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 700, color: COLORS.gold }}>{language === 'pt' ? 'Liquidez Total' : 'Total Liquidity'}</span>
            <span style={{ fontFamily: FONTS.sans, fontSize: 18, fontWeight: 700, color: COLORS.gold }}>{d.liquidezTotal.toFixed(1)}M</span>
          </div>
        </div>
      </div>

      {/* Right Panel: Coverage Ratios */}
      <div style={{ position: 'absolute', right: 60, top: 140, width: '38%', bottom: 100, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ backgroundColor: COLORS.contentBg, borderRadius: 16, padding: '24px 30px' }}>
          <h3 style={{ fontFamily: FONTS.sans, fontSize: 12, fontWeight: 700, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '1px', margin: 0, marginBottom: 20 }}>
            {language === 'pt' ? 'RÁCIOS DE COBERTURA' : 'COVERAGE RATIOS'}
          </h3>
          {ratios.map((ratio, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 16, padding: '12px 16px', backgroundColor: '#FFFFFF', borderRadius: 8 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: FONTS.sans, fontSize: 11, color: COLORS.textSecondary, margin: 0, marginBottom: 4 }}>{ratio.label}</p>
                <p style={{ fontFamily: FONTS.serif, fontSize: 22, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>{ratio.value}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary }}>Target: {ratio.target}</span>
                <div style={{ marginTop: 4, padding: '3px 10px', backgroundColor: ratio.status === 'ok' ? 'rgba(22, 163, 74, 0.1)' : 'rgba(196, 30, 109, 0.1)', borderRadius: 4 }}>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 10, fontWeight: 600, color: ratio.status === 'ok' ? COLORS.positive : COLORS.negative }}>
                    {ratio.status === 'ok' ? '✓ OK' : '⚠'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Editorial */}
        <div style={{ backgroundColor: 'rgba(200, 161, 58, 0.1)', borderRadius: 12, padding: '20px 24px', borderLeft: `4px solid ${COLORS.gold}` }}>
          <p style={{ fontFamily: FONTS.serif, fontSize: 14, fontStyle: 'italic', color: COLORS.textWhite, margin: 0, lineHeight: 1.6 }}>
            {language === 'pt' 
              ? '"Todos os rácios de liquidez acima dos covenants bancários, com folga significativa."'
              : '"All liquidity ratios above bank covenants, with significant headroom."'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>{language === 'pt' ? 'ABC | Liquidez e Fluxo de Caixa' : 'ABC | Liquidity & Cash Flow'}</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.gold, fontWeight: 600 }}>Board-ready</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>3</span>
      </div>
    </div>
  );
};

// ============ SLIDE 4: DÍVIDA E INVESTIMENTO ============
const DebtInvestmentSlide = ({ data, language = 'pt' }) => {
  const d = data || {
    dividaBruta: 95.2,
    caixa: 40.8,
    dividaLiquida: 54.4,
    amortTrim: 5.2,
    amortAnual: 22.5,
    capexTrim: 8.5,
    capexAnual: 38.0,
    capexOrc: 42.0,
  };

  return (
    <div style={{ width: 1600, height: 900, backgroundColor: COLORS.pageBg, position: 'relative', fontFamily: FONTS.sans, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ position: 'absolute', left: 60, top: 48, right: 60 }}>
        <h1 style={{ fontFamily: FONTS.serif, fontSize: 34, fontWeight: 700, color: COLORS.textWhite, margin: 0 }}>
          {language === 'pt' ? 'Dívida e Investimento' : 'Debt & Investment'}
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 15, color: '#64748B', marginTop: 12 }}>
          {language === 'pt' ? 'Estrutura de dívida e execução do plano de investimento — 1.º Trimestre 2026' : 'Debt structure and investment plan execution — Q1 2026'}
        </p>
      </div>

      {/* Left: Debt Structure */}
      <div style={{ position: 'absolute', left: 60, top: 140, width: '47%', bottom: 160, backgroundColor: COLORS.contentBg, borderRadius: 16, padding: '30px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 4, height: 24, backgroundColor: COLORS.gold, borderRadius: 2 }} />
          <h2 style={{ fontFamily: FONTS.serif, fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>
            {language === 'pt' ? 'Estrutura de Dívida' : 'Debt Structure'}
          </h2>
        </div>

        {/* Debt Stack */}
        <div style={{ marginBottom: 30 }}>
          {[
            { label: language === 'pt' ? 'Dívida Bruta' : 'Gross Debt', value: d.dividaBruta, color: '#C41E6D' },
            { label: language === 'pt' ? '(-) Caixa' : '(-) Cash', value: -d.caixa, color: COLORS.positive },
            { label: language === 'pt' ? '= Dívida Líquida' : '= Net Debt', value: d.dividaLiquida, color: COLORS.textPrimary, bold: true },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: item.bold ? 700 : 400, color: COLORS.textPrimary }}>{item.label}</span>
              <span style={{ fontFamily: FONTS.sans, fontSize: 16, fontWeight: 700, color: item.color }}>USD {Math.abs(item.value).toFixed(1)}M</span>
            </div>
          ))}
        </div>

        {/* Amortization Schedule */}
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: 10, padding: '16px 20px' }}>
          <p style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 700, color: COLORS.gold, textTransform: 'uppercase', margin: 0, marginBottom: 12 }}>
            {language === 'pt' ? 'AMORTIZAÇÕES' : 'AMORTIZATIONS'}
          </p>
          <div style={{ display: 'flex', gap: 20 }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary, margin: 0 }}>{language === 'pt' ? '1.º Trim.' : 'Q1'}</p>
              <p style={{ fontFamily: FONTS.serif, fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>USD {d.amortTrim}M</p>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary, margin: 0 }}>{language === 'pt' ? 'Anual 2026' : 'Full Year 2026'}</p>
              <p style={{ fontFamily: FONTS.serif, fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>USD {d.amortAnual}M</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: CAPEX */}
      <div style={{ position: 'absolute', right: 60, top: 140, width: '47%', bottom: 160, backgroundColor: COLORS.contentBg, borderRadius: 16, padding: '30px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 4, height: 24, backgroundColor: COLORS.gold, borderRadius: 2 }} />
          <h2 style={{ fontFamily: FONTS.serif, fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>
            {language === 'pt' ? 'Execução de CAPEX' : 'CAPEX Execution'}
          </h2>
        </div>

        {/* CAPEX Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 10, padding: '20px' }}>
            <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary, margin: 0, marginBottom: 8 }}>{language === 'pt' ? 'CAPEX 1.º Trim.' : 'CAPEX Q1'}</p>
            <p style={{ fontFamily: FONTS.serif, fontSize: 26, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>USD {d.capexTrim}M</p>
          </div>
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: 10, padding: '20px' }}>
            <p style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary, margin: 0, marginBottom: 8 }}>{language === 'pt' ? 'CAPEX Anual' : 'Annual CAPEX'}</p>
            <p style={{ fontFamily: FONTS.serif, fontSize: 26, fontWeight: 700, color: COLORS.textPrimary, margin: 0 }}>USD {d.capexAnual}M</p>
          </div>
        </div>

        {/* Budget vs Actual */}
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: 10, padding: '20px' }}>
          <p style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 700, color: COLORS.textSecondary, margin: 0, marginBottom: 16 }}>
            {language === 'pt' ? 'EXECUÇÃO VS ORÇAMENTO' : 'EXECUTION VS BUDGET'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ height: 24, backgroundColor: '#E5E7EB', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                <div style={{ 
                  height: '100%', 
                  width: `${(d.capexAnual / d.capexOrc) * 100}%`, 
                  backgroundColor: COLORS.gold, 
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: 8
                }}>
                  <span style={{ fontFamily: FONTS.sans, fontSize: 10, fontWeight: 700, color: '#FFFFFF' }}>
                    {Math.round((d.capexAnual / d.capexOrc) * 100)}%
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary }}>{language === 'pt' ? 'Executado' : 'Executed'}: USD {d.capexAnual}M</span>
                <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textSecondary }}>{language === 'pt' ? 'Orçado' : 'Budget'}: USD {d.capexOrc}M</span>
              </div>
            </div>
          </div>
        </div>

        {/* Editorial */}
        <div style={{ marginTop: 20, padding: '16px 20px', backgroundColor: 'rgba(200, 161, 58, 0.08)', borderRadius: 8, borderLeft: `3px solid ${COLORS.gold}` }}>
          <p style={{ fontFamily: FONTS.serif, fontSize: 13, fontStyle: 'italic', color: COLORS.textSecondary, margin: 0 }}>
            {language === 'pt' 
              ? '"CAPEX em linha com cronograma. Expansão da lavaria no prazo."'
              : '"CAPEX on schedule. Plant expansion on track."'}
          </p>
        </div>
      </div>

      {/* Message Cards */}
      <div style={{ position: 'absolute', left: 60, bottom: 56, right: 60, display: 'flex', gap: 20 }}>
        {[
          { num: '1', title: language === 'pt' ? 'Dívida sob controlo' : 'Debt under control', text: language === 'pt' ? 'Alavancagem abaixo do covenant' : 'Leverage below covenant' },
          { num: '2', title: language === 'pt' ? 'Amortização coberta' : 'Amortization covered', text: language === 'pt' ? 'FCO cobre serviço da dívida' : 'FCO covers debt service' },
          { num: '3', title: language === 'pt' ? 'CAPEX financiado' : 'CAPEX funded', text: language === 'pt' ? 'Sem necessidade de nova dívida' : 'No new debt required' },
        ].map((msg, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: '#FFFFFF', borderRadius: 8, padding: '16px 20px', borderTop: `3px solid ${COLORS.gold}` }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: COLORS.pageBg, color: COLORS.textWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{msg.num}</div>
            <p style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, margin: 0, marginBottom: 4 }}>{msg.title}</p>
            <p style={{ fontFamily: FONTS.sans, fontSize: 12, color: COLORS.textSecondary, margin: 0 }}>{msg.text}</p>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>{language === 'pt' ? 'ABC | Liquidez e Fluxo de Caixa' : 'ABC | Liquidity & Cash Flow'}</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.gold, fontWeight: 600 }}>Board-ready</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>4</span>
      </div>
    </div>
  );
};

// ============ SLIDE 5: RESUMO EXECUTIVO ============
const ExecutiveSummarySlide = ({ data, language = 'pt' }) => {
  const conclusions = language === 'pt' ? [
    { icon: '1', title: 'Liquidez Robusta', desc: 'FCO acima do orçamento com taxa de conversão de 60%. Posição de caixa permite cumprir todas as obrigações.', highlight: 'FCO +28% vs Orç' },
    { icon: '2', title: 'Rácios Confortáveis', desc: 'Todos os indicadores de liquidez acima dos covenants bancários, com margem significativa de segurança.', highlight: 'Liquidez 2.1x' },
    { icon: '3', title: 'Investimento Financiado', desc: 'CAPEX em execução conforme plano, financiado integralmente pelo FCO sem necessidade de nova dívida.', highlight: 'Dív.Líq./EBITDA 0.8x' },
  ] : [
    { icon: '1', title: 'Robust Liquidity', desc: 'FCO above budget with 60% conversion rate. Cash position covers all obligations.', highlight: 'FCO +28% vs Bgt' },
    { icon: '2', title: 'Comfortable Ratios', desc: 'All liquidity indicators above bank covenants with significant safety margin.', highlight: 'Liquidity 2.1x' },
    { icon: '3', title: 'Funded Investment', desc: 'CAPEX executing per plan, fully funded by FCO with no new debt required.', highlight: 'Net Debt/EBITDA 0.8x' },
  ];

  const priorities = language === 'pt' ? [
    'Manter disciplina de capital de giro no pico de actividade',
    'Assegurar execução do CAPEX dentro do envelope orçado',
    'Preservar folga de liquidez para oportunidades',
  ] : [
    'Maintain working capital discipline during peak activity',
    'Ensure CAPEX execution within budget envelope',
    'Preserve liquidity headroom for opportunities',
  ];

  return (
    <div style={{ width: 1600, height: 900, backgroundColor: COLORS.pageBg, position: 'relative', fontFamily: FONTS.sans, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ position: 'absolute', left: 60, top: 48, right: 60 }}>
        <h1 style={{ fontFamily: FONTS.serif, fontSize: 34, fontWeight: 700, color: COLORS.textWhite, margin: 0 }}>
          {language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        </h1>
        <p style={{ fontFamily: FONTS.sans, fontSize: 15, color: '#64748B', marginTop: 12 }}>
          {language === 'pt' ? 'Principais conclusões e prioridades de gestão' : 'Key conclusions and management priorities'}
        </p>
      </div>

      {/* Conclusions */}
      <div style={{ position: 'absolute', left: 60, top: 140, right: 60, display: 'flex', gap: 24 }}>
        {conclusions.map((c, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: COLORS.contentBg, borderRadius: 12, padding: '28px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: COLORS.pageBg, color: COLORS.textWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans, fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{c.icon}</div>
            <h3 style={{ fontFamily: FONTS.serif, fontSize: 18, fontWeight: 700, color: COLORS.textPrimary, margin: 0, marginBottom: 12 }}>{c.title}</h3>
            <p style={{ fontFamily: FONTS.sans, fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6, margin: 0, marginBottom: 16, flex: 1 }}>{c.desc}</p>
            <div style={{ backgroundColor: 'rgba(200, 161, 58, 0.1)', borderLeft: `3px solid ${COLORS.gold}`, padding: '10px 14px', borderRadius: '0 6px 6px 0' }}>
              <span style={{ fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, color: COLORS.gold }}>{c.highlight}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Priorities */}
      <div style={{ position: 'absolute', left: 60, top: 500, right: 60 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 4, height: 24, backgroundColor: COLORS.gold, borderRadius: 2 }} />
          <h2 style={{ fontFamily: FONTS.serif, fontSize: 18, fontWeight: 700, color: COLORS.textWhite, margin: 0 }}>
            {language === 'pt' ? 'Prioridades de Gestão' : 'Management Priorities'}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          {priorities.map((p, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14, padding: '18px 22px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 10, borderLeft: `3px solid ${COLORS.gold}` }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: COLORS.gold, color: COLORS.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
              <span style={{ fontFamily: FONTS.sans, fontSize: 13, fontWeight: 600, color: COLORS.textWhite, lineHeight: 1.4 }}>{p}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Closing Statement */}
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 80 }}>
        <div style={{ backgroundColor: 'rgba(200, 161, 58, 0.08)', borderRadius: 12, padding: '22px 30px', borderLeft: `4px solid ${COLORS.gold}` }}>
          <p style={{ fontFamily: FONTS.serif, fontSize: 17, fontStyle: 'italic', color: COLORS.textWhite, margin: 0, lineHeight: 1.6 }}>
            {language === 'pt' 
              ? '"A posição de liquidez confirma a solidez financeira da operação. O desafio passa por manter a disciplina de capital de giro durante o ciclo de expansão."'
              : '"The liquidity position confirms operational financial strength. The challenge is maintaining working capital discipline during the expansion cycle."'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 60, right: 60, bottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>{language === 'pt' ? 'ABC | Liquidez e Fluxo de Caixa' : 'ABC | Liquidity & Cash Flow'}</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.gold, fontWeight: 600 }}>Board-ready</span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: '#64748B' }}>5</span>
      </div>
    </div>
  );
};

// ============ MAIN PREVIEW COMPONENT ============
const LiquidityReportPreview = ({ 
  reportData, 
  language = 'pt', 
  onClose, 
  onDownload,
  isDownloading = false 
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = 5;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && currentSlide < totalSlides - 1) {
        setCurrentSlide(prev => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      } else if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, onClose]);

  const nextSlide = () => currentSlide < totalSlides - 1 && setCurrentSlide(currentSlide + 1);
  const prevSlide = () => currentSlide > 0 && setCurrentSlide(currentSlide - 1);

  const slides = [
    <CoverSlide key="cover" data={reportData} language={language} />,
    <CashFlowSlide key="cashflow" data={reportData} language={language} />,
    <LiquidityPositionSlide key="position" data={reportData} language={language} />,
    <DebtInvestmentSlide key="debt" data={reportData} language={language} />,
    <ExecutiveSummarySlide key="summary" data={reportData} language={language} />,
  ];

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-50">
        <X className="w-8 h-8" />
      </button>
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">{currentSlide + 1} de {totalSlides}</div>
      <button onClick={prevSlide} disabled={currentSlide === 0} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
        <ChevronLeft className="w-8 h-8 text-white" />
      </button>
      <button onClick={nextSlide} disabled={currentSlide === totalSlides - 1} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
        <ChevronRight className="w-8 h-8 text-white" />
      </button>
      <div className="w-full max-w-6xl aspect-video rounded-lg overflow-hidden shadow-2xl" style={{ maxHeight: '80vh' }}>
        <div className="w-full h-full flex items-center justify-center bg-[#0A192F] overflow-hidden">
          <div className="transform scale-[0.56] origin-center">{slides[currentSlide]}</div>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4">
        <div className="flex gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-[#C5A059] w-6' : 'bg-white/30 hover:bg-white/50'}`} />
          ))}
        </div>
        <button onClick={onDownload} disabled={isDownloading} className="flex items-center gap-2 bg-[#C5A059] hover:bg-[#B8934D] text-white font-semibold px-6 py-2.5 rounded-lg transition-all disabled:opacity-50 ml-4">
          <Download className="w-4 h-4" />
          {isDownloading ? (language === 'pt' ? 'A gerar...' : 'Generating...') : 'PDF'}
        </button>
      </div>
    </div>
  );
};

export default LiquidityReportPreview;
